@if (isLoading$ | async) {
  <div class="spinner">
    <mat-progress-spinner color="primary" mode="indeterminate"> </mat-progress-spinner>
  </div>
}

@if ((isLoading$ | async) === false) {
  <section class="finding">
    <header class="finding-header">
      <div class="finding-header-title">
        <span class="finding-header-title-text" appLongTextTooltip>
          {{ findingModel.rule.displayName }}
        </span>

        <div class="finding-header-secondary-title">
          <span>{{
            (isTaintAnalysis ? 'aiInLineControlPage.runtimeControl' : 'aiFindingPage.runtimeFinding') | labelsProvider
          }}</span>
          @if (isUnifiedPipelineEnabled) {
            <div class="separator">|</div>
            <app-status-label [statusLabel]="statusLabel()" />
          }
          <div class="separator">|</div>
          <app-severity-label [severity]="findingModel.rule.severity" [smallSize]="true" />

          <div class="separator">|</div>
          <div class="finding-header-secondary-title-property">
            {{ findingModel.timestamp | date: 'MMM d, y, HH:mm:ss' }}
          </div>

          <div class="separator">|</div>
          <div class="finding-header-secondary-title-property">
            <div>{{ 'aiFindingPage.idTitle' | labelsProvider }}</div>
            <app-copy-button
              class="copy-button"
              [value]="findingModel.id"
              [title]="findingModel.id"
              [isSecondaryFontColor]="true"
            >
            </app-copy-button>
          </div>
        </div>

        <div class="finding-header-quaternary-title">
          <div class="finding-header-quaternary-title-property finding-header-quaternary-title-property-clickable">
            <app-hyperlink-button
              buttonIconPath="assets/images/common/label.svg"
              buttonTextPath="labelsDialog.setLabelsTitle"
              (buttonClickedEventEmitter)="addLabelsClicked()"
            ></app-hyperlink-button>
            <div class="separator">|</div>
            <app-view-labels-multi-select
              [labelBindings]="findingModel.labelBindings || []"
              (removeLabelEventEmitter)="removeLabelBindingFromFinding($event)"
            ></app-view-labels-multi-select>
            @if (isUpdatingLabels$ | async) {
              <mat-progress-spinner class="small-spinner" color="primary" mode="indeterminate"></mat-progress-spinner>
            }
            @if (menuActions.length > 0) {
              <div class="separator">|</div>
              <app-single-page-actions
                [menuActions]="menuActions"
                (actionSelected)="onActionSelected($event)"
              ></app-single-page-actions>
            }
          </div>
        </div>
      </div>

      <app-sidepanel-tabs class="finding-header-tabs" [tabs]="tabs" (tabOnSelection)="onTabSelect($event)">
      </app-sidepanel-tabs>
    </header>

    <div class="finding-body">
      @if (activeTabIndex === 0) {
        <div class="finding-body-tab">
          <div class="finding-body-tab-card finding-details-card">
            <div class="finding-body-tab-card-title">
              {{ 'aiFindingPage.summaryCardTitle' | labelsProvider }}
            </div>
            <div>
              {{ findingModel.rule.summary }}
            </div>
            <app-card-fields [cardFields]="findingDetailsCardFields"></app-card-fields>
            @if (!isTaintAnalysis) {
              <app-anchor-link navigation="" (clicked)="navigateToThreadFindings()">
                <span appLongTextTooltip>{{ 'aiFindingPage.viewRelatedFindings' | labelsProvider }}</span>
              </app-anchor-link>

              @if (
                relevantStepsCardFieldList.length >= 1 &&
                (!findingModel.evidence || findingModel.evidence.length < 1 || findingModel.rule.id === 'ZN_M00031')
              ) {
                <app-carousel-card
                  class="step-body-tab-card-inner"
                  [cardFieldsList]="relevantStepsCardFieldList"
                  titleLabelPath="aiFindingPage.relevantAgentStepsCardTitle"
                >
                </app-carousel-card>
              }
            }
          </div>

          @if (isTaintAnalysis) {
            @if (taintAnalysisEvidence.length > 0) {
              <div class="finding-body-tab-card evidence-card">
                <app-taint-analysis-evidence [evidence]="taintAnalysisEvidence"></app-taint-analysis-evidence>
              </div>
            } @else {
              <div class="finding-body-tab-card evidence-card">
                <div class="finding-body-tab-card-title">
                  {{ 'aiInLineControlPage.evidenceTitle' | labelsProvider }}
                </div>
                <div>{{ 'aiInLineControlPage.evidenceNoData' | labelsProvider }}</div>
              </div>
            }
          }

          @if (!isTaintAnalysis) {
            @if (findingModel.evidence && findingModel.evidence.length >= 1 && findingModel.rule.id !== 'ZN_M00031') {
              <div class="finding-body-tab-card evidence-card">
                <div class="finding-body-tab-card-title">Evidence</div>
                <app-evidence-list
                  [evidenceList]="findingModel.evidence"
                  [relatedSteps]="findingModel.relatedSteps"
                  [findingId]="findingModel.id"
                  [integrationId]="integration()"
                  [sourcePipeline]="findingModel.sourcePipeline"
                ></app-evidence-list>
              </div>
            }

            <div class="finding-body-tab-card actor-card">
              <div class="finding-body-tab-card-title">Actor Details</div>
              <app-card-fields [cardFields]="actorCardFields"></app-card-fields>
            </div>

            <div class="finding-body-tab-card risk-card">
              <div class="finding-body-tab-card-title">
                {{ 'aiFindingPage.riskReasonCardTitle' | labelsProvider }}
              </div>
              <div>{{ this.findingModel.rule.riskReason }}</div>

              <app-security-tag [tags]="owaspRiskTag"></app-security-tag>

              <app-security-tag [tags]="owaspAsiRiskTag"></app-security-tag>

              <app-security-tag [tags]="owaspNhiRiskTag"></app-security-tag>

              @if (attackRiskTag.length > 0) {
                <app-security-tag [tags]="attackRiskTag"></app-security-tag>
              }
            </div>

            <div class="finding-body-tab-card triage-card">
              <div class="finding-body-tab-card-title">
                {{ 'aiFindingPage.investigateSuggestionCardTitle' | labelsProvider }}
              </div>
              @if (triageSuggestion.length > 1) {
                <app-timeline [timelineItems]="triageSuggestion" [withIcons]="false"></app-timeline>
              }
              @if (triageSuggestion.length === 1) {
                <div>
                  {{ this.findingModel.rule.triageSuggestion }}
                </div>
              }
            </div>

            @if (this.findingModel.rule.fixSuggestion !== '') {
              <div class="finding-body-tab-card fix-card">
                <div class="finding-body-tab-card-title">
                  {{ 'aiFindingPage.fixSuggestionCardTitle' | labelsProvider }}
                </div>
                @if (fixSuggestion.length > 1) {
                  <app-timeline [timelineItems]="fixSuggestion" [withIcons]="false"> </app-timeline>
                }
                @if (fixSuggestion.length === 1) {
                  <div>
                    {{ this.findingModel.rule.fixSuggestion }}
                  </div>
                }
              </div>
            }
          }
        </div>
      }
    </div>

    @if (showNoData && (isLoading$ | async) === false) {
      <div class="no-data-container">
        <app-no-data [inDialog]="true"></app-no-data>
      </div>
    }
  </section>
}

<ng-template #customAgentTemplate>
  <div class="custom-agent-container">
    @if (getAiServiceIconPath() !== '') {
      <img [src]="getAiServiceIconPath()" alt="AI Service Icon" class="ai-service-icon" />
    }
    @if (hasInventoryId()) {
      <app-anchor-link navigation="" (clicked)="navigateToAgentFromCustomTemplate()" class="field-link-value">
        <span class="field-link-value__text" appLongTextTooltip>{{
          findingModel.relatedSteps[0].botDetails.displayName
        }}</span>
      </app-anchor-link>
    } @else {
      <span class="agent-name-text" appLongTextTooltip>{{ findingModel.relatedSteps[0].botDetails.displayName }}</span>
    }
    <span class="extension-agent-text"> ({{ 'aiAgentStepPage.m365CopilotExtensionAgent' | labelsProvider }})</span>
    <img
      src="assets/images/common/info.svg"
      alt="Info"
      class="tooltip-icon"
      [matTooltip]="'aiAgentStepPage.m365CopilotExtensionAgentTooltip' | labelsProvider"
      matTooltipPosition="above"
      matTooltipClass="tooltip-container"
    />
  </div>
</ng-template>
